/*
 * This unpublished material is proprietary to Vaticle.
 * All rights reserved. The methods and
 * techniques described herein are considered trade secrets
 * and/or confidential. Reproduction or distribution, in whole
 * or in part, is forbidden except by express written permission
 * of Vaticle.
 */

import { Component } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { Subject, delay, switchMap } from "rxjs";
import {
    ButtonComponent, ConfirmationModalComponent, ConfirmationModalData,
    ContactSupportDialogData,
    ContactSupportModalComponent,
    DeleteResourceSectionComponent,
    DatetimePipe, IntegrationCardComponent,
    MenuItem,
    PropertiesTableComponent, PropertiesTableRowComponent
} from "typedb-cloud-framework";
import { TeamUpdateDialogComponent } from "../../update/team-update-dialog.component";

import { ApplicationState } from "../../../../service/application-state.service";
import { SnackbarService } from "../../../../service/snackbar.service";
import { TablePaginatorComponent, TableToolbarAction, TableToolbarComponent } from "../../../../framework/table";
import { MatTooltipModule } from "@angular/material/tooltip";
import { ApiTokensTableComponent } from "../../api-token/table/api-tokens-table.component";
import { TeamController } from "../../../../service/team/team-controller.service";
import { TeamApi } from "../../../../service/team/team-api.service";
import { APITokenCreationDialogComponent } from "../../api-token/create/api-token-creation-dialog.component";
import { APIToken, TeamAPIToken } from "../../../../concept/api-token";
import { membersPath } from "../../../../routing/resource-paths";
import { RouterLink } from "@angular/router";

@Component({
    selector: "tp-team-settings-general",
    templateUrl: "./team-settings-general.component.html",
    styleUrls: ["./team-settings-general.component.scss", "../settings.scss"],
    imports: [
        PropertiesTableComponent, ButtonComponent, DeleteResourceSectionComponent,
        TablePaginatorComponent, PropertiesTableRowComponent,
        DatetimePipe, RouterLink,
        MatTooltipModule, IntegrationCardComponent,
        ApiTokensTableComponent, TableToolbarComponent,
    ]
})
export class TeamSettingsGeneralComponent {
    readonly team = this.app.requireCurrentTeam();
    teamIsPersonal = this.team.isPersonal;

    creating = false;
    apiTokenCreationButton: TableToolbarAction = {
        text: "Create API Token",
        enabled: !this.creating,
        onClick: () => { this.openAPITokenCreationModal(); }
    };

    private readonly unsubAPITokens$ = new Subject<void>();
    readonly apiTokens = this.teamCtl.apiTokensTable(this.unsubAPITokens$);

    constructor(
        private app: ApplicationState, private snackbar: SnackbarService, private dialog: MatDialog,
        private teamCtl: TeamController, private teamApi: TeamApi,
    ) { }

    readonly apiTokensTableRowPopupMenuItems: (apiToken: TeamAPIToken) => MenuItem[] = (apiToken) => {
        return [
            {
                label: "Revoke API Token",
                action: () => this.openAPITokenRevokeModal(apiToken),
                dangerous: true
            },
        ];
    }

    openAPITokenRevokeModal(apiToken: APIToken) {
        const modal = this.dialog.open<ConfirmationModalComponent, ConfirmationModalData>(ConfirmationModalComponent, {
            data: {
                title: "Revoke API Token",
                body: `Are you sure you would like to revoke access for '${apiToken.name}'? Make sure to remove it from any live integrations first.`,
                confirmText: `Revoke API Token`,
                confirmButtonStyle: "primary-outline red stroke",
            },
        }).componentInstance;
        modal.confirmed.subscribe(() => {
            this.teamApi.revokeAPIToken(apiToken.uuid).subscribe(() => {
                this.snackbar.success("API Token successfully revoked");
                modal.close();
            })
        })
    }

    openAPITokenCreationModal() {
        this.dialog.open(APITokenCreationDialogComponent, { width: "580px" });
    }

    openEditTeamDialog() {
        this.dialog.open(TeamUpdateDialogComponent);
    }

    openEnableTeamModeModal() {
        const modal = this.dialog.open<ConfirmationModalComponent, ConfirmationModalData>(ConfirmationModalComponent, {
           data: {
               title: "Enable member invites",
               body: "Are you sure you would like to enable member invites?",
           }
        }).componentInstance;
        modal.confirmed.pipe(
            delay(1000),
            switchMap(() => this.teamApi.updateTeam({
                uuid: this.team.uuid,
                isPersonal: false,
            }))
        ).subscribe({
            next: () => {
                modal.close();
                this.snackbar.success("You can now invite team members on the members page");
                this.teamIsPersonal = false;
            },
            error: () => {
                modal.isSubmitting$.next(false);
            }
        });
    }

    showContactSupportDialog() {
        this.dialog.open<ContactSupportModalComponent, ContactSupportDialogData>(ContactSupportModalComponent, { data: { title: "Delete team" } });
    }

    // TODO
    // deleteTeam() {
    //     const currentTeam = this.app.requireCurrentTeam();
    //     this.teamService.deleteTeam(currentTeam.uuid).subscribe(() => {
    //         this.snackbar.success(`Team '${currentTeam.id}' is now deleted.`);
    //         this.router.navigate(["/"]);
    //     });
    // }

    protected readonly membersPath = membersPath;
}
